import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  Injector,
  input,
  model,
  output,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type { Subject } from 'rxjs';
import { timer } from 'rxjs';
import { ONYX_TRANSITION_DURATION } from '../../../constants';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import type { OnyxOptional } from '../../../interfaces';
import { I18N_NAMESPACE } from '../../../internal/constants';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxLoadingErrorComponent } from '../../error/onyx-loading-error/onyx-loading-error.component';
import { OnyxSpinnerComponent } from '../../spinner/onyx-spinner/onyx-spinner.component';
import { OnyxTooltipComponent } from '../../tooltip/onyx-tooltip/onyx-tooltip.component';

@Component({
  selector: 'onyx-modal',
  imports: [
    OnyxIconButtonComponent,
    OnyxIconButtonComponent,
    RouterModule,
    OnyxLoadingErrorComponent,
    OnyxSpinnerComponent,
    TranslatePipe,
    OnyxTooltipDirective,
    NgTemplateOutlet,
    OnyxTooltipComponent,
    DecimalPipe,
  ],
  templateUrl: './onyx-modal.component.html',
  styleUrl: './onyx-modal.component.scss',
  host: {
    '[class.left]': `variant() === 'left'`,
    '[class.right]': `variant() === 'right'`,
    '[class.background]': `variant() === 'center' && backdrop()`,
    '[class.no-backdrop]': `variant() === 'center' && !backdrop()`,
    '[class.show-animation]': `showAnimation()`,
    '[class.close-animation]': `closeAnimation()`,
  },
})
export class OnyxModalComponent<T> implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly renderer = inject(Renderer2);

  public readonly variant = input.required<'left' | 'center' | 'right'>();
  public readonly size = input<420 | 480 | 640 | 980>(640);
  public readonly heading = input<OnyxOptional<string>>(null);
  public readonly headingTooltip = input<string | null>(null);
  public readonly color = input<'white' | 'gray'>('white');
  public readonly loading = input(false);
  public readonly error = model(false);
  public readonly count = input<number>();
  public readonly gap = input(6);
  public readonly showClose = input(true);
  public readonly closeDisabled = input(false);
  public readonly showTopBar = input(true);
  public readonly showAnimation = input(true);
  public readonly showBottomBar = input<boolean>();
  public readonly scrollbarGutter = input(true);
  public readonly fitContent = input(false);
  public readonly resize = input(true);
  public readonly backdrop = input(true);
  public readonly close$ = input<Subject<T>>();

  public readonly closeModal = output<T | undefined>();

  protected readonly I18N = `${I18N_NAMESPACE}.modal`;

  protected closeAnimation = signal(false);
  protected showLoading = signal(false);
  protected isHeaderSticky = signal(false);
  protected isFooterSticky = signal(false);

  protected isCenter = computed(() => this.variant() === 'center');

  private dialogElementRef =
    viewChild.required<ElementRef<HTMLElement>>('dialogElement');

  public ngAfterViewInit(): void {
    const element = this.dialogElementRef().nativeElement;
    if (this.resize()) return;

    const height = element.offsetHeight;
    this.renderer.setStyle(element, 'height', `${height}px`);
  }

  public ngOnInit(): void {
    effect(
      () => {
        this.showLoading.set(false);
        if (this.loading()) {
          timer(1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.showLoading.set(true));
        }
      },
      { injector: this.injector },
    );

    this.close$()
      ?.asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.closeDialog.bind(this));
  }

  @HostListener('onyxModalClose')
  protected requestClose(): void {
    if (!this.closeDisabled()) this.closeDialog();
  }

  protected closeDialog(value?: T): void {
    this.closeAnimation.set(true);
    timer(ONYX_TRANSITION_DURATION)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeModal.emit(value));
  }

  @HostListener('onyxModalForceClose')
  protected forceCloseDialog(): void {
    this.closeModal.emit(undefined);
  }

  protected handleContentScroll(content: HTMLElement): void {
    this.isHeaderSticky.set(content.scrollTop > 0);
    this.isFooterSticky.set(
      content.scrollHeight > content.clientHeight &&
        content.scrollTop < content.scrollHeight - content.clientHeight,
    );
  }
}
